import { Stack, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { UserContext } from '../Hooks/useUser';
import { useGetInventoryInsightsQuery } from '../../api/inventorySlice';
import { useNavigate } from 'react-router';
import InsightTable from './InsightTable';
import { theme } from '../../theme';

function Insights() {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);

    const { data: inventoryInsights = {}, isLoading: isLoadingInsights } = useGetInventoryInsightsQuery({
        hospitalId: user.hospital.id
    });

    // seperate bulk load into four insight tables
    const restockItems = inventoryInsights['most_restocked'] || [];
    const runItems = inventoryInsights['most_run_for'] || [];
    const trashedItems = inventoryInsights['most_trashed'] || [];
    const skuInsights = inventoryInsights['sku_insights'] || [];

    function handleItemClick(item) {
        navigate(`/organizations/${user.hospital.organization_id}/items/${item.item_id}/itemAnalytics`);
    }

    // Column configurations for different table types
    const restockColumns = [
        { key: 'item_name', header: 'Item' },
        { key: 'num_cases', header: 'Cases' },
        { key: 'restock_quantity', header: 'Restock Qty' },
        { key: 'picked_quantity', header: 'Picked Qty' },
        { 
            key: 'restock_rate', 
            header: 'Restock Rate',
            render: (item) => (
                <Typography 
                    variant="body2" 
                    color={item.restock_quantity / item.picked_quantity > 0.1 ? 'error' : 'success'}
                    fontWeight="600"
                >
                    {((item.restock_quantity / item.picked_quantity) * 100).toFixed(1)}%
                </Typography>
            )
        }
    ];

    const runColumns = [
        { key: 'item_name', header: 'Item' },
        { key: 'num_cases', header: 'Cases', render: (item) => item.num_cases || 0 },
        { key: 'run_quantity', header: 'Run Quantity', render: (item) => item.run_quantity || 0 },
        { 
            key: 'run_rate', 
            header: 'Run Rate',
            render: (item) => (
                <Typography 
                    variant="body2" 
                    color={item.run_quantity / item.picked_quantity > 0.4 ? 'error' : item.run_quantity / item.picked_quantity  > 0.2 ? 'warning' : 'text.secondary'}
                    fontWeight="600"
                >
                    {(item.run_quantity / item.picked_quantity * 100) ? `${(item.run_quantity / item.picked_quantity * 100).toFixed(1)}%` : 'N/A'}
                </Typography>
            )
        }
    ];

    const trashedColumns = [
        { key: 'item_name', header: 'Item' },
        { key: 'num_cases', header: 'Cases', render: (item) => item.num_cases || 0 },
        { key: 'open_quantity', header: 'Opened Qty', render: (item) => item.open_quantity || 0 },
        { key: 'trashed_quantity', header: 'Trashed Qty', render: (item) => item.trashed_quantity || 0 },
        { 
            key: 'trash_rate', 
            header: 'Trash Rate',
            render: (item) => (
                <Typography 
                    variant="body2" 
                    color={item.trashed_quantity / item.open_quantity > 0.15 ? 'error' : item.trashed_quantity / item.open_quantity  > 0.08 ? 'warning' : 'text.secondary'}
                    fontWeight="600"
                >
                    {((item.trashed_quantity / item.open_quantity) * 100).toFixed(1)}%
                </Typography>
            )
        }
    ];

    const skuInsightsColumn = [
        { key: 'item_name', header: 'Item' },
        { key: 'num_cases', header: 'Cases', render: (item) => item.num_cases || 0 },
        { key: 'num_physicians', header: 'Physicians', render: (item) => item.num_physicians || 0 },
        { key: 'num_cards', header: 'Cards', render: (item) => item.num_cards || 0 }
    ];

    return (
        <Stack spacing={2}>
            <Stack sx={{ backgroundColor: theme.palette.background.white, top: 64, position: 'sticky', zIndex: 5, borderBottom: '1px solid', borderColor: 'divider' }} px={4} py={2}>
                <Typography variant="h5" fontWeight="600">Inventory Insights</Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    width="100%"
                    spacing={{ xs: 0.5, sm: 2 }}
                >
                    <Typography variant="subtitle1" color="text.secondary">{user.hospital.name}</Typography>
                    <Typography variant="subtitle1" color="text.secondary" textAlign={{ xs: 'left', sm: 'right' }}>
                        Insights over the last <Typography component="span" fontWeight={700} color="text.secondary">12 months</Typography>
                    </Typography>
                </Stack>
            </Stack>
            <Stack spacing={2} px={4} py={2}>
                <Stack direction='row' spacing={4}>
                    <InsightTable 
                        title="Frequently Restocked Items"
                        subtitle="Items at this location with the highest rates of restocking after being picked."
                        data={restockItems}
                        isLoading={isLoadingInsights}
                        columns={restockColumns}
                        onRowClick={handleItemClick}
                        emptyMessage="No restock data available"
                    />
                    <InsightTable 
                        title="Frequently Trashed Items"
                        subtitle="Items at this location most commonly opened but not used."
                        data={trashedItems}
                        isLoading={isLoadingInsights}
                        columns={trashedColumns}
                        onRowClick={handleItemClick}
                        emptyMessage="No trashed items data available"
                    />
                </Stack>
                
                <Stack direction='row' spacing={4}>
                    <InsightTable 
                        title="Frequently Run-For Items"
                        subtitle="Items at this location that are most often not ready in the OR when needed and require a run."
                        data={runItems}
                        isLoading={isLoadingInsights}
                        columns={runColumns}
                        onRowClick={handleItemClick}
                        emptyMessage="No run data available"
                    />

                    <InsightTable
                        title="SKU Elimination Opportunity"
                        subtitle="Items at this location that are the least commonly used across physicians and cards."
                        data={skuInsights}
                        isLoading={isLoadingInsights}
                        columns={skuInsightsColumn}
                        onRowClick={handleItemClick}
                        emptyMessage="No sku elimination opportunity data available"
                    />
                </Stack>
            </Stack>
        </Stack>
    );
}

export default Insights;

